import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Message } from "@cowork/shared";
import { cn, formatTokens, formatCost } from "../../lib/utils.js";
import { PROVIDER_INFOS } from "@cowork/shared";

interface Props {
  message: Message;
  onRegenerate?: () => void;
  onOpenArtifact?: (code: string, language: string, title: string) => void;
  isLast?: boolean;
}

function estimateCost(message: Message): number {
  if (!message.tokenUsage || !message.provider || !message.model) return 0;
  const info = PROVIDER_INFOS[message.provider];
  const modelInfo = info?.models.find((m) => m.id === message.model);
  if (!modelInfo) return 0;
  return (
    (message.tokenUsage.inputTokens * modelInfo.inputCostPer1M +
      message.tokenUsage.outputTokens * modelInfo.outputCostPer1M) /
    1_000_000
  );
}

/** Claude's logo mark — orange circle with concentric ring */
function ClaudeAvatar() {
  return (
    <div
      className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
      style={{ background: "var(--accent)" }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" fill="none" />
        <circle cx="7" cy="7" r="2" fill="rgba(255,255,255,0.95)" />
      </svg>
    </div>
  );
}

export function MessageBubble({ message, onRegenerate, onOpenArtifact, isLast }: Props) {
  const isUser = message.role === "user";
  const text = message.content.find((b) => b.type === "text")?.text ?? "";
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const cost = estimateCost(message);
  const usage = message.tokenUsage;

  if (isUser) {
    return (
      <div className="group flex justify-end px-4 py-2 gap-3">
        <div className="flex flex-col items-end max-w-[75%]">
          <div
            className="px-4 py-3 rounded-2xl rounded-tr-sm text-sm leading-relaxed"
            style={{ background: "var(--bg-tertiary)", color: "var(--text-primary)" }}
          >
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{text}</pre>
          </div>
          {message.fileCards && message.fileCards.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-1.5 w-full">
              {message.fileCards.map((fc) => (
                <button
                  key={fc.filePath}
                  onClick={() => fetch(`http://localhost:3001/open?path=${encodeURIComponent(fc.filePath)}`)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs text-left transition-colors"
                  style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="flex-shrink-0" style={{ color: "var(--accent)" }}>
                    <path d="M3 1h6l3 3v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                    <path d="M9 1v3h3" />
                  </svg>
                  <span className="truncate" style={{ color: "var(--text-primary)" }}>{fc.fileName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Assistant message — no bubble, just flowing text with avatar
  return (
    <div className="group flex gap-4 px-4 py-3">
      <ClaudeAvatar />

      <div className="flex-1 min-w-0 max-w-[85%]">
        <div
          className="text-sm leading-relaxed prose-sm"
          style={{ color: "var(--text-primary)" }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-3 last:mb-0 leading-7">{children}</p>,
              h1: ({ children }) => <h1 className="text-base font-semibold mb-2 mt-4 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h1>,
              h2: ({ children }) => <h2 className="text-sm font-semibold mb-1.5 mt-4 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-medium mb-1 mt-3 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h3>,
              ul: ({ children }) => <ul className="mb-3 space-y-1 pl-4" style={{ listStyleType: "disc" }}>{children}</ul>,
              ol: ({ children }) => <ol className="mb-3 space-y-1 pl-4" style={{ listStyleType: "decimal" }}>{children}</ol>,
              li: ({ children }) => <li className="text-sm leading-6" style={{ color: "var(--text-primary)" }}>{children}</li>,
              code: ({ children, className }) => {
                const lang = className?.replace("language-", "") ?? "";
                const isBlock = Boolean(className?.includes("language-"));
                const codeStr = String(children).replace(/\n$/, "");
                return isBlock ? (
                  <div className="my-3 rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                    <div className="flex items-center justify-between px-4 py-1.5" style={{ background: "var(--bg-tertiary)", borderBottom: "1px solid var(--border)" }}>
                      <span className="text-[10px] font-mono" style={{ color: "var(--text-tertiary)" }}>{lang || "code"}</span>
                      {onOpenArtifact && (
                        <button
                          onClick={() => onOpenArtifact(codeStr, lang, lang ? `${lang} snippet` : "Code snippet")}
                          className="flex items-center gap-1 text-[10px] transition-colors"
                          style={{ color: "var(--accent)" }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.75"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                        >
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 1h4l2 2v6a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" /><path d="M6 1v2h2" /></svg>
                          Open
                        </button>
                      )}
                    </div>
                    <code
                      className="block px-4 py-3 text-xs font-mono overflow-x-auto whitespace-pre"
                      style={{ background: "var(--bg-secondary)", color: "var(--text-primary)" }}
                    >{children}</code>
                  </div>
                ) : (
                  <code
                    className="rounded px-1.5 py-0.5 text-xs font-mono"
                    style={{ background: "var(--bg-tertiary)", color: "var(--accent)" }}
                  >{children}</code>
                );
              },
              pre: ({ children }) => <>{children}</>,
              table: ({ children }) => (
                <div className="overflow-x-auto my-3 rounded-xl" style={{ border: "1px solid var(--border)" }}>
                  <table className="text-xs border-collapse w-full">{children}</table>
                </div>
              ),
              th: ({ children }) => (
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-3 py-2 text-xs" style={{ borderBottom: "1px solid var(--border-subtle)", color: "var(--text-primary)" }}>
                  {children}
                </td>
              ),
              blockquote: ({ children }) => (
                <blockquote className="pl-4 my-3 text-sm" style={{ borderLeft: "2px solid var(--accent)", color: "var(--text-secondary)" }}>
                  {children}
                </blockquote>
              ),
              strong: ({ children }) => <strong className="font-semibold" style={{ color: "var(--text-primary)" }}>{children}</strong>,
              hr: () => <hr className="my-4" style={{ borderColor: "var(--border)" }} />,
              a: ({ children, href }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" style={{ color: "var(--accent)" }}>
                  {children}
                </a>
              ),
            }}
          >
            {text}
          </ReactMarkdown>
        </div>

        {/* Footer — tokens + inline controls */}
        <div className="flex items-center gap-2 mt-2">
          {usage && (
            <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
              {formatTokens(usage.inputTokens + usage.outputTokens)} tokens
              {cost > 0 && <> · {formatCost(cost)}</>}
            </span>
          )}

          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ActionBtn title="Copy" onClick={handleCopy}>
              {copied ? (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="2,6 5,9 10,3" /></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="4" y="4" width="7" height="7" rx="1" />
                  <path d="M8 4V2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2" />
                </svg>
              )}
            </ActionBtn>

            <ActionBtn
              title="Good response"
              onClick={() => setFeedback(feedback === "up" ? null : "up")}
              active={feedback === "up"}
              activeColor="#4ade80"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 11V6l3-5h.5a.5.5 0 0 1 .5.5V4h3a1 1 0 0 1 1 1l-.5 5H2z" />
              </svg>
            </ActionBtn>

            <ActionBtn
              title="Bad response"
              onClick={() => setFeedback(feedback === "down" ? null : "down")}
              active={feedback === "down"}
              activeColor="#f87171"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M10 1v5L7 11h-.5a.5.5 0 0 1-.5-.5V8H3a1 1 0 0 1-1-1l.5-5H10z" />
              </svg>
            </ActionBtn>

            {isLast && onRegenerate && (
              <ActionBtn title="Regenerate" onClick={onRegenerate}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M10 6a4 4 0 1 1-1.2-2.8" />
                  <polyline points="10,1 10,4 7,4" />
                </svg>
              </ActionBtn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({
  children, title, onClick, active, activeColor,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  active?: boolean;
  activeColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="w-6 h-6 flex items-center justify-center rounded-md transition-colors"
      style={{
        color: active ? activeColor : "var(--text-tertiary)",
      }}
      onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      {children}
    </button>
  );
}

/** Tool call in-flight indicator */
export function ToolCallIndicator({ toolName }: { toolName: string }) {
  return (
    <div className="flex gap-4 px-4 py-2">
      <ClaudeAvatar />
      <div
        className="flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs"
        style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}
      >
        <span className="animate-pulse w-1.5 h-1.5 rounded-full inline-block" style={{ background: "var(--accent)" }} />
        <span>Calling <code className="font-mono" style={{ color: "var(--accent)" }}>{toolName}</code>…</span>
      </div>
    </div>
  );
}

export function StreamingBubble({ text }: { text: string }) {
  return (
    <div className="flex gap-4 px-4 py-3">
      <div
        className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 animate-pulse"
        style={{ background: "var(--accent)" }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" fill="none" />
          <circle cx="7" cy="7" r="2" fill="rgba(255,255,255,0.95)" />
        </svg>
      </div>
      <div className="flex-1 min-w-0 max-w-[85%] text-sm leading-relaxed" style={{ color: "var(--text-primary)" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p className="mb-3 last:mb-0 leading-7">{children}</p>,
            code: ({ children, className }) => {
              const isBlock = className?.includes("language-");
              return isBlock
                ? <code className="block rounded-xl px-4 py-3 text-xs font-mono my-3 overflow-x-auto whitespace-pre" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>{children}</code>
                : <code className="rounded px-1.5 py-0.5 text-xs font-mono" style={{ background: "var(--bg-tertiary)", color: "var(--accent)" }}>{children}</code>;
            },
            pre: ({ children }) => <>{children}</>,
          }}
        >{text}</ReactMarkdown>
        <span className="animate-pulse" style={{ color: "var(--accent)" }}>▊</span>
      </div>
    </div>
  );
}
